import PropTypes from 'prop-types'
import {getIn, Field} from 'formik'
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
} from '@chakra-ui/react'
import isValidAbn from 'is-valid-abn'
import {SolidTickedIcon} from '~/react/icons'

const AbnVerificationField = ({
  label,
  initialAbn,
  verificationStatus,
  entityName,
  onVerifyClick,
  onAbnChange,
  verifyButtonLabel,
  retryButtonLabel,
}) => {
  const renderRightElement = (currentValue, hasFormatError) => {
    const abnChanged = currentValue !== initialAbn && currentValue !== ''
    const handleVerifyClick = () => onVerifyClick(currentValue)

    if (verificationStatus === 'loading') {
      return <Spinner size="sm" color="mld.neutral.700" />
    }

    if (verificationStatus === 'invalid') {
      return (
        <Button
          variant="tertiary"
          size="xs"
          onClick={handleVerifyClick}
        >
          {retryButtonLabel}
        </Button>
      )
    }

    if (abnChanged && verificationStatus !== 'valid') {
      return (
        <Button
          variant="tertiary"
          size="xs"
          isDisabled={hasFormatError}
          onClick={handleVerifyClick}
        >
          {verifyButtonLabel}
        </Button>
      )
    }

    return null
  }

  return (
    <Field name="businessNumber">
      {({field, form: {touched, errors}}) => {
        const hasFormatError = !isValidAbn(field.value)
        const view = renderRightElement(field.value, hasFormatError)

        return (
          <FormControl
            pb="sm"
            isInvalid={Boolean(
              getIn(touched, 'businessNumber') &&
                getIn(errors, 'businessNumber'),
            )}
          >
            {label && (
              <FormLabel>
                <Text textStyle="body-copy-medium">{label}</Text>
              </FormLabel>
            )}
            <InputGroup size="lg">
              <Input
                size="lg"
                fontSize="base"
                type="text"
                name={field.name}
                onChange={(e) => {
                  field.onChange(e)
                  onAbnChange(e.target.value)
                }}
                onBlur={field.onBlur}
                value={field.value}
                pr={view ? '5.5rem' : undefined}
              />
              {view && (
                <InputRightElement width="5rem" pr="sm">
                  {view}
                </InputRightElement>
              )}
            </InputGroup>
            {verificationStatus === 'valid' && entityName && (
              <FormHelperText>
                <Flex align="center" gap="xs">
                  <SolidTickedIcon
                    color="mld.success.700"
                    boxSize="4"
                    data-testid="abn-verified-icon"
                  />
                  <Text textStyle="body-detail" color="mld.neutral.700">
                    {entityName}
                  </Text>
                </Flex>
              </FormHelperText>
            )}
            <FormErrorMessage justifyContent="end" textStyle="body-detail">
              <Text textStyle="body-detail">
                {getIn(errors, 'businessNumber')}
              </Text>
            </FormErrorMessage>
          </FormControl>
        )
      }}
    </Field>
  )
}

AbnVerificationField.defaultProps = {
  label: null,
  initialAbn: '',
  entityName: null,
}

AbnVerificationField.propTypes = {
  label: PropTypes.string,
  initialAbn: PropTypes.string,
  verificationStatus: PropTypes.oneOf(['idle', 'loading', 'valid', 'invalid'])
    .isRequired,
  entityName: PropTypes.string,
  onVerifyClick: PropTypes.func.isRequired,
  onAbnChange: PropTypes.func.isRequired,
  verifyButtonLabel: PropTypes.string.isRequired,
  retryButtonLabel: PropTypes.string.isRequired,
}

export default AbnVerificationField
